import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Mail, Plus, Save, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import {
  getCorreoEscenarios,
  setCorreoEscenarios,
  type EscenarioResumen,
} from "@/lib/api";

/**
 * Configuración → Envío de correo de escenarios.
 *
 * Elige qué escenario se convierte en el PDF "Reporte Comercial · Pipeline y
 * Portafolio" que el Job manda cada día hábil a las 8:00, y a quién. La
 * configuración se guarda en etrm.configuracion (clave reporte_comercial);
 * el Job la lee en cada corrida, así que el cambio aplica al siguiente envío
 * sin redeploy.
 */
export function CorreoEscenariosPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin", "correo-escenarios"], queryFn: getCorreoEscenarios });

  const [activo, setActivo] = useState(true);
  const [idEscenario, setIdEscenario] = useState<number | null>(null);
  const [destinatarios, setDestinatarios] = useState<string[]>([]);
  const [nuevo, setNuevo] = useState("");
  const [guardadoOk, setGuardadoOk] = useState(false);

  // Cargar el formulario con lo vigente cada vez que llega del servidor.
  useEffect(() => {
    if (!q.data) return;
    setActivo(q.data.config.activo);
    setIdEscenario(q.data.config.id_escenario);
    setDestinatarios(q.data.config.destinatarios);
  }, [q.data]);

  const guardar = useMutation({
    mutationFn: () =>
      setCorreoEscenarios({ activo, id_escenario: idEscenario, destinatarios }),
    onSuccess: () => {
      setGuardadoOk(true);
      qc.invalidateQueries({ queryKey: ["admin", "correo-escenarios"] });
      setTimeout(() => setGuardadoOk(false), 3000);
    },
  });

  const sucio = useMemo(() => {
    if (!q.data) return false;
    const c = q.data.config;
    return (
      c.activo !== activo ||
      c.id_escenario !== idEscenario ||
      c.destinatarios.join("|") !== destinatarios.join("|")
    );
  }, [q.data, activo, idEscenario, destinatarios]);

  // Escenarios agrupados por dueño (los ve el admin aunque no se los compartan).
  const porDueno = useMemo(() => {
    const m = new Map<string, EscenarioResumen[]>();
    for (const e of q.data?.escenarios ?? []) {
      const k = e.creado_por.toLowerCase();
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(e);
    }
    return [...m.entries()];
  }, [q.data]);

  const seleccionado = q.data?.escenarios.find((e) => e.id === idEscenario) ?? null;

  const agregarCorreo = () => {
    const c = nuevo.trim();
    if (!c) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c)) return;
    if (destinatarios.some((d) => d.toLowerCase() === c.toLowerCase())) {
      setNuevo("");
      return;
    }
    setDestinatarios([...destinatarios, c]);
    setNuevo("");
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Envío de correo de escenarios</h1>
        <p className="text-sm text-slate-500 mt-1">
          Elige el escenario que se manda cada día como{" "}
          <span className="font-medium text-slate-700">
            Reporte Comercial · Pipeline y Portafolio
          </span>{" "}
          y a quién le llega. Cada persona recibe su propia copia del PDF con marca de agua.
        </p>
        {q.data && (
          <p className="text-xs text-slate-400 mt-1">Horario de envío: {q.data.horario}.</p>
        )}
      </div>

      {q.isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          {(q.error as Error)?.message}
        </div>
      )}

      {q.data && !q.data.tabla_lista && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-3 text-sm">
          La tabla de configuración aún no existe en la base de datos (migración 090). Se
          muestran los valores actuales del Job, pero no se podrá guardar hasta aplicarla.
        </div>
      )}

      {q.data && (
        <>
          {/* Activo / inactivo */}
          <section className="bg-white border border-slate-200 rounded-lg p-4 flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-slate-800">Envío diario</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {activo
                  ? "El Job genera y envía el reporte cada día hábil."
                  : "Pausado: el Job corre pero no envía nada."}
              </div>
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <span className="text-xs text-slate-500">{activo ? "Activo" : "Pausado"}</span>
              <button
                type="button"
                role="switch"
                aria-checked={activo}
                onClick={() => setActivo(!activo)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  activo ? "bg-emerald-500" : "bg-slate-300"
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    activo ? "translate-x-5" : "translate-x-0.5"
                  }`}
                />
              </button>
            </label>
          </section>

          {/* Escenario */}
          <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-slate-800">Escenario que se envía</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Se listan los escenarios de todos los usuarios. El PDF se genera con la misma
                  lógica del botón "Reporte PDF" de la página Escenarios.
                </div>
              </div>
              {seleccionado && (
                <div className="text-xs text-right text-slate-500">
                  Seleccionado:{" "}
                  <span className="font-medium text-slate-800">{seleccionado.nombre}</span>
                  <div>{seleccionado.creado_por}</div>
                </div>
              )}
            </div>
            {porDueno.length === 0 && (
              <div className="p-4 text-sm text-slate-500">No hay escenarios guardados.</div>
            )}
            {porDueno.map(([dueno, lista]) => (
              <div key={dueno}>
                <div className="px-4 py-1.5 bg-slate-50 text-xs font-medium text-slate-500 border-b border-slate-100">
                  {dueno}
                </div>
                <ul className="divide-y divide-slate-100">
                  {lista.map((e) => {
                    const sel = e.id === idEscenario;
                    return (
                      <li key={e.id}>
                        <label
                          className={`flex items-start gap-3 px-4 py-2.5 cursor-pointer hover:bg-slate-50 ${
                            sel ? "bg-emerald-50/60" : ""
                          }`}
                        >
                          <input
                            type="radio"
                            name="escenario"
                            className="mt-1 accent-emerald-600"
                            checked={sel}
                            onChange={() => setIdEscenario(e.id)}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-slate-800 font-medium truncate">
                              {e.nombre}
                            </div>
                            {e.descripcion && (
                              <div className="text-xs text-slate-500 truncate">{e.descripcion}</div>
                            )}
                            <div className="text-xs text-slate-400 mt-0.5">
                              {e.n_operaciones} operación{e.n_operaciones === 1 ? "" : "es"} ·
                              actualizado {fechaCorta(e.actualizado_en)}
                            </div>
                          </div>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </section>

          {/* Destinatarios */}
          <section className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
            <div>
              <div className="text-sm font-medium text-slate-800">Destinatarios</div>
              <div className="text-xs text-slate-500 mt-0.5">
                Un correo individual por persona, con su copia del PDF marcada con su dirección.
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {destinatarios.length === 0 && (
                <span className="text-xs text-slate-400">Sin destinatarios.</span>
              )}
              {destinatarios.map((d) => (
                <span
                  key={d}
                  className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-700 text-xs px-2.5 py-1"
                >
                  <Mail className="h-3 w-3 text-slate-400" />
                  {d}
                  <button
                    type="button"
                    title="Quitar"
                    className="ml-0.5 text-slate-400 hover:text-red-600"
                    onClick={() => setDestinatarios(destinatarios.filter((x) => x !== d))}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            <form
              className="flex gap-2"
              onSubmit={(ev) => {
                ev.preventDefault();
                agregarCorreo();
              }}
            >
              <input
                type="email"
                value={nuevo}
                onChange={(ev) => setNuevo(ev.target.value)}
                placeholder="nombre@mppenergia.com"
                className="flex-1 rounded border border-slate-300 px-3 py-1.5 text-sm"
              />
              <button
                type="submit"
                disabled={!nuevo.trim()}
                className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                <Plus className="h-4 w-4" /> Agregar
              </button>
            </form>
          </section>

          {/* Guardar */}
          <div className="flex items-center justify-between gap-4">
            <div className="text-xs text-slate-500">
              {q.data.guardada && q.data.config.actualizado_en ? (
                <>
                  Última modificación: {q.data.config.actualizado_por} ·{" "}
                  {fechaCorta(q.data.config.actualizado_en)}
                </>
              ) : (
                <>Aún no se ha guardado nada: se muestran los valores por defecto del Job.</>
              )}
            </div>
            <div className="flex items-center gap-3">
              {guardar.isError && (
                <span className="text-sm text-red-600">{(guardar.error as Error).message}</span>
              )}
              {guardadoOk && !sucio && (
                <span className="inline-flex items-center gap-1 text-sm text-emerald-700">
                  <CheckCircle2 className="h-4 w-4" /> Guardado. Aplica al siguiente envío.
                </span>
              )}
              <button
                type="button"
                onClick={() => guardar.mutate()}
                disabled={!sucio || guardar.isPending || !q.data.tabla_lista}
                className="inline-flex items-center gap-1.5 rounded bg-slate-900 text-white px-4 py-2 text-sm hover:bg-slate-800 disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {guardar.isPending ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function fechaCorta(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
